import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
    ClipboardList, Plus, Search, Loader2, Clock, Play, CheckCircle2,
    Settings2, Calendar, Building2, Users, FileText, LayoutGrid, List,
    BarChart3, ChevronDown, ChevronRight, Sparkles, User as UserIcon,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
    prep: { label: 'Preparing', color: 'bg-amber-100 text-amber-700', icon: Clock },
    live: { label: 'Live', color: 'bg-red-100 text-red-700', icon: Play },
    processing: { label: 'Processing', color: 'bg-blue-100 text-blue-700', icon: Settings2 },
    completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
};

const DATE_RANGES = [
    { value: '7', label: 'Last 7 days' },
    { value: '30', label: 'Last 30 days' },
    { value: '90', label: 'Last 90 days' },
    { value: 'all', label: 'All time' },
];

function dateRangeToFilter(range) {
    if (range === 'all') return {};
    const days = parseInt(range, 10);
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days);
    return {
        date_from: from.toISOString(),
        date_to: to.toISOString(),
    };
}

function AnalysisBadge() {
    return (
        <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-100 text-violet-700"
            title="Post-meeting analysis available"
        >
            <Sparkles className="w-3 h-3" /> Analysis
        </span>
    );
}

function SessionCard({ session, onClick }) {
    const statusConf = STATUS_CONFIG[session.status] || STATUS_CONFIG.prep;
    const StatusIcon = statusConf.icon;
    const answeredCount = (session.question_progress || []).filter(q => q.status === 'answered').length;
    const totalQuestions = (session.question_progress || []).length;
    const hasAnalysis = !!session.post_meeting;

    return (
        <div
            className="bg-white border border-stone-200 rounded-xl p-5 hover:shadow-md hover:border-stone-300 transition-all cursor-pointer"
            onClick={onClick}
        >
            <div className="flex items-center justify-between mb-3 gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusConf.color}`}>
                        <StatusIcon className="w-3 h-3" /> {statusConf.label}
                    </span>
                    {hasAnalysis && <AnalysisBadge />}
                </div>
                {session.duration_minutes ? (
                    <span className="text-[10px] text-stone-400 whitespace-nowrap">{Math.round(session.duration_minutes)} min</span>
                ) : null}
            </div>

            <h3 className="text-sm font-bold text-stone-900 truncate mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {session.title || 'Untitled Meeting'}
            </h3>

            <div className="flex flex-wrap items-center gap-3 text-[11px] text-stone-500 mt-2">
                {session.deal_id && (
                    <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> Deal linked</span>
                )}
                {session.account_id && (
                    <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> Company</span>
                )}
                {session.contact_ids?.length > 0 && (
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {session.contact_ids.length} attendees</span>
                )}
            </div>

            <div className="flex items-center gap-4 text-[10px] text-stone-400 pt-3 mt-3 border-t border-stone-100">
                {totalQuestions > 0 && (
                    <span>{answeredCount}/{totalQuestions} questions answered</span>
                )}
                <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {session.created_at ? new Date(session.created_at).toLocaleDateString() : ''}
                </span>
            </div>
        </div>
    );
}

function SessionRow({ session, onClick, repLookup }) {
    const statusConf = STATUS_CONFIG[session.status] || STATUS_CONFIG.prep;
    const StatusIcon = statusConf.icon;
    const answered = (session.question_progress || []).filter(q => q.status === 'answered').length;
    const total = (session.question_progress || []).length;
    const hasAnalysis = !!session.post_meeting;
    const repName = repLookup[session.user_id] || '—';

    return (
        <button
            onClick={onClick}
            className="w-full grid grid-cols-[auto_1fr_auto_auto_auto_auto_auto] items-center gap-3 px-4 py-2.5 border-b border-stone-100 hover:bg-stone-50 text-left transition-colors"
            data-testid="session-row"
        >
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusConf.color}`}>
                <StatusIcon className="w-3 h-3" /> {statusConf.label}
            </span>
            <span className="text-sm font-medium text-stone-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {session.title || 'Untitled Meeting'}
            </span>
            {hasAnalysis ? <AnalysisBadge /> : <span className="text-[10px] text-stone-300">—</span>}
            <span className="flex items-center gap-1 text-[11px] text-stone-500">
                <UserIcon className="w-3 h-3" /> {repName}
            </span>
            <span className="text-[11px] text-stone-500 whitespace-nowrap">
                {total > 0 ? `${answered}/${total} Q` : '—'}
            </span>
            <span className="text-[11px] text-stone-500 whitespace-nowrap">
                {session.duration_minutes ? `${Math.round(session.duration_minutes)}m` : '—'}
            </span>
            <span className="text-[11px] text-stone-500 whitespace-nowrap">
                {session.created_at ? new Date(session.created_at).toLocaleDateString() : ''}
            </span>
        </button>
    );
}

function KpiTile({ label, value, hint }) {
    return (
        <div className="bg-white border border-stone-200 rounded-xl p-4">
            <div className="text-[11px] uppercase tracking-wide text-stone-500 font-medium">{label}</div>
            <div className="text-2xl font-bold text-stone-900 mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</div>
            {hint && <div className="text-[11px] text-stone-400 mt-0.5">{hint}</div>}
        </div>
    );
}

function TimeSeriesBars({ series }) {
    if (!series || series.length === 0) {
        return <div className="text-xs text-stone-400 italic">No sessions in this range</div>;
    }
    const max = Math.max(...series.map(p => p.count), 1);
    return (
        <div className="flex items-end gap-1 h-24" data-testid="time-series-bars">
            {series.map(p => (
                <div key={p.date} className="flex-1 flex flex-col items-center gap-1 group" title={`${p.date}: ${p.count}`}>
                    <div
                        className="w-full bg-stone-300 group-hover:bg-stone-900 rounded-t transition-colors"
                        style={{ height: `${(p.count / max) * 100}%`, minHeight: p.count > 0 ? '3px' : '1px' }}
                    />
                    <div className="text-[9px] text-stone-400 truncate w-full text-center">
                        {p.date.slice(5)}
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function MeetingSessions() {
    const { user, getAuthHeader } = useAuth();
    const navigate = useNavigate();
    const isAdmin = !!(user?.is_admin);

    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterUserId, setFilterUserId] = useState('all');
    const [viewMode, setViewMode] = useState(() => {
        try { return localStorage.getItem('meeting_sessions_view') || 'list'; } catch { return 'list'; }
    });
    const [dashboardOpen, setDashboardOpen] = useState(true);
    const [dateRange, setDateRange] = useState('30');

    const [teamMembers, setTeamMembers] = useState([]);
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);

    useEffect(() => {
        try { localStorage.setItem('meeting_sessions_view', viewMode); } catch {}
    }, [viewMode]);

    // Team members — needed for admin rep filter + row rep names
    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`${API}/accounts/team-members`, { headers: getAuthHeader() });
                setTeamMembers(Array.isArray(res.data) ? res.data : []);
            } catch {
                setTeamMembers([]);
            }
        })();
    }, [getAuthHeader]);

    const repLookup = useMemo(() => {
        const m = {};
        for (const t of teamMembers) {
            m[t.user_id] = t.name || t.email || t.user_id;
        }
        return m;
    }, [teamMembers]);

    const loadSessions = useCallback(async () => {
        setLoading(true);
        try {
            const params = { ...dateRangeToFilter(dateRange) };
            if (filterStatus) params.status = filterStatus;
            if (isAdmin && filterUserId && filterUserId !== 'all') params.user_id = filterUserId;
            const res = await axios.get(`${API}/meeting-sessions`, { headers: getAuthHeader(), params });
            setSessions(res.data.sessions || []);
        } catch (err) {
            toast.error('Failed to load meeting sessions');
        } finally {
            setLoading(false);
        }
    }, [getAuthHeader, dateRange, filterStatus, filterUserId, isAdmin]);

    const loadStats = useCallback(async () => {
        setStatsLoading(true);
        try {
            const params = { ...dateRangeToFilter(dateRange) };
            if (isAdmin && filterUserId && filterUserId !== 'all') params.user_id = filterUserId;
            const res = await axios.get(`${API}/meeting-sessions/stats`, { headers: getAuthHeader(), params });
            setStats(res.data);
        } catch (err) {
            setStats(null);
        } finally {
            setStatsLoading(false);
        }
    }, [getAuthHeader, dateRange, filterUserId, isAdmin]);

    useEffect(() => { loadSessions(); }, [loadSessions]);
    useEffect(() => { if (dashboardOpen) loadStats(); }, [loadStats, dashboardOpen]);

    const createSession = async () => {
        try {
            const res = await axios.post(`${API}/meeting-sessions`, { title: 'New Meeting' }, { headers: getAuthHeader() });
            navigate(`/meeting-sessions/${res.data.session_id}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create session');
        }
    };

    const filtered = sessions.filter(s => {
        if (search && !(s.title || '').toLowerCase().includes(search.toLowerCase())) return false;
        return true;
    });

    return (
        <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Meeting Sessions
                    </h1>
                    <p className="text-sm text-stone-500 mt-1">
                        Track and review your sales meeting recordings, transcripts, and outcomes
                    </p>
                </div>
                <Button onClick={createSession} className="gap-2">
                    <Plus className="w-4 h-4" /> New Session
                </Button>
            </div>

            {/* Dashboard */}
            <div className="bg-white border border-stone-200 rounded-xl">
                <button
                    onClick={() => setDashboardOpen(o => !o)}
                    className="w-full flex items-center justify-between px-5 py-3 text-left"
                    data-testid="dashboard-toggle"
                >
                    <div className="flex items-center gap-2">
                        {dashboardOpen ? <ChevronDown className="w-4 h-4 text-stone-500" /> : <ChevronRight className="w-4 h-4 text-stone-500" />}
                        <BarChart3 className="w-4 h-4 text-stone-600" />
                        <span className="text-sm font-semibold text-stone-900">Dashboard</span>
                    </div>
                    <div onClick={e => e.stopPropagation()}>
                        <Select value={dateRange} onValueChange={setDateRange}>
                            <SelectTrigger className="w-40 h-8 text-xs">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {DATE_RANGES.map(r => (
                                    <SelectItem key={r.value} value={r.value} className="text-xs">{r.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </button>

                {dashboardOpen && (
                    <div className="border-t border-stone-100 p-5 space-y-5">
                        {statsLoading && !stats ? (
                            <div className="flex items-center justify-center h-24">
                                <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
                            </div>
                        ) : stats ? (
                            <>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <KpiTile label="Total Meetings" value={stats.total} />
                                    <KpiTile label="Completed" value={stats.status_counts?.completed ?? 0} />
                                    <KpiTile label="Total Minutes" value={Math.round(stats.total_minutes || 0)} />
                                    <KpiTile label="Analysis Ready" value={stats.analysis_ready ?? 0} hint="Post-meeting insights" />
                                </div>

                                <div>
                                    <div className="text-[11px] uppercase tracking-wide text-stone-500 font-medium mb-2">
                                        Meetings per day
                                    </div>
                                    <TimeSeriesBars series={stats.time_series || []} />
                                </div>

                                {isAdmin && stats.per_rep?.length > 0 && (
                                    <div>
                                        <div className="text-[11px] uppercase tracking-wide text-stone-500 font-medium mb-2">
                                            By sales rep
                                        </div>
                                        <div className="border border-stone-100 rounded-lg overflow-hidden" data-testid="per-rep-table">
                                            <table className="w-full text-xs">
                                                <thead className="bg-stone-50 text-stone-500">
                                                    <tr>
                                                        <th className="text-left px-3 py-2 font-medium">Rep</th>
                                                        <th className="text-right px-3 py-2 font-medium">Meetings</th>
                                                        <th className="text-right px-3 py-2 font-medium">Completed</th>
                                                        <th className="text-right px-3 py-2 font-medium">Minutes</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {stats.per_rep.map(r => (
                                                        <tr key={r.user_id} className="border-t border-stone-100 hover:bg-stone-50">
                                                            <td className="px-3 py-2 text-stone-900">{r.name}</td>
                                                            <td className="px-3 py-2 text-right text-stone-700">{r.count}</td>
                                                            <td className="px-3 py-2 text-right text-stone-700">{r.completed}</td>
                                                            <td className="px-3 py-2 text-right text-stone-700">{Math.round(r.minutes)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="text-xs text-stone-400 italic">Stats unavailable</div>
                        )}
                    </div>
                )}
            </div>

            {/* Toolbar */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search sessions..." className="pl-9" />
                </div>
                <div className="flex items-center gap-1">
                    {['', 'prep', 'live', 'processing', 'completed'].map(s => (
                        <button
                            key={s}
                            onClick={() => setFilterStatus(s)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                filterStatus === s ? 'bg-stone-900 text-white' : 'text-stone-500 hover:bg-stone-100 hover:text-stone-700'
                            }`}
                        >
                            {s ? (STATUS_CONFIG[s]?.label || s) : 'All'}
                        </button>
                    ))}
                </div>

                {isAdmin && (
                    <Select value={filterUserId} onValueChange={setFilterUserId}>
                        <SelectTrigger className="w-48 h-9" data-testid="rep-filter">
                            <SelectValue placeholder="All reps" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All reps</SelectItem>
                            {teamMembers.map(t => (
                                <SelectItem key={t.user_id} value={t.user_id}>
                                    {t.name || t.email}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}

                <div className="ml-auto flex items-center bg-stone-100 rounded-lg p-0.5" data-testid="view-toggle">
                    <button
                        onClick={() => setViewMode('list')}
                        className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                            viewMode === 'list' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500'
                        }`}
                        title="List view"
                    >
                        <List className="w-3.5 h-3.5" /> List
                    </button>
                    <button
                        onClick={() => setViewMode('cards')}
                        className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                            viewMode === 'cards' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500'
                        }`}
                        title="Card view"
                    >
                        <LayoutGrid className="w-3.5 h-3.5" /> Cards
                    </button>
                </div>
            </div>

            {/* Sessions */}
            {loading ? (
                <div className="flex items-center justify-center h-48">
                    <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
                </div>
            ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 text-stone-400">
                    <ClipboardList className="w-10 h-10 mb-3 text-stone-300" />
                    <p className="text-sm font-medium">
                        {sessions.length === 0 ? 'No meeting sessions yet' : 'No matching sessions'}
                    </p>
                    {sessions.length === 0 && (
                        <p className="text-xs mt-1">Start a new meeting session or connect from the desktop app</p>
                    )}
                </div>
            ) : viewMode === 'cards' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.map(s => (
                        <SessionCard
                            key={s.session_id}
                            session={s}
                            onClick={() => navigate(`/meeting-sessions/${s.session_id}`)}
                        />
                    ))}
                </div>
            ) : (
                <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                    <div className="grid grid-cols-[auto_1fr_auto_auto_auto_auto_auto] items-center gap-3 px-4 py-2 bg-stone-50 border-b border-stone-200 text-[10px] uppercase tracking-wide text-stone-500 font-semibold">
                        <span>Status</span>
                        <span>Title</span>
                        <span>Analysis</span>
                        <span>Rep</span>
                        <span className="whitespace-nowrap">Questions</span>
                        <span>Duration</span>
                        <span>Date</span>
                    </div>
                    {filtered.map(s => (
                        <SessionRow
                            key={s.session_id}
                            session={s}
                            repLookup={repLookup}
                            onClick={() => navigate(`/meeting-sessions/${s.session_id}`)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
